import { useCallback, useEffect, useRef, useState } from 'react';

import { doRequest } from '@r1-frontend/do-request';
import { ISuccessResponse } from '@r1-frontend/api-domru/phone-validator/validation/dto/ISuccessResponse';
import { TValidationType } from '@r1-frontend/api-domru/phone-validator/validation/dto/TValidationType';
import { validate } from '@r1-frontend/api-domru/phone-validator/validation/validate';

import { Paragraph4 } from '@r1-frontend/ui-react/components/typography/paragraph';
import { TTimeStatus } from '@r1-frontend/shared/hooks/useTimer';

import { ButtonWithTimer } from '~/src/components/ButtonWithTimer';

import * as ST from './styled';

export const CODE_LENGTH = 4;

interface IProps {
    isCodeSended: boolean,
    phone: string,
    verifyType: TValidationType,
    timeStatus: TTimeStatus,
    seconds: number,
    start: (countSeconds: number) => void,
    stop: () => void,
    onSendSms: () => Promise<void>, // запрос кода, вызывается при клике на кнопке с таймером
    onConfirmed: () => void, // подключение пакета, вызывается после успешной валидации
}

const PhoneVerifyForm = ({
    isCodeSended,
    phone,
    verifyType,
    timeStatus,
    seconds,
    start,
    stop,
    onSendSms,
    onConfirmed,
}: IProps): JSX.Element => {
    const inputRef = useRef<HTMLInputElement | null>(null);

    const [smsCode, setSmsCode] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isSmsRequestOnceSent, setIsSmsRequestOnceSent] = useState(true);
    const [codeValidationErrorMessage, setCodeValidationErrorMessage] = useState('');

    const onRequestCode = useCallback(() => async() => {
        setIsSmsRequestOnceSent(false);
        setIsLoading(true);

        setSmsCode('');

        await onSendSms();

        setIsLoading(false);
    }, [onSendSms]);

    const onValidateCode = useCallback(async(smsValue: string) => {
        setCodeValidationErrorMessage('');
        setIsLoading(true);

        const resp = await doRequest.phoneValidator<ISuccessResponse>(
            validate({
                phone,
                code: smsValue,
            }),
        );

        if (resp.isSuccess && resp.payload.success) {
            setSmsCode('');
            onConfirmed();
        } else {
            setCodeValidationErrorMessage('Неверно. Попробуйте ещё раз.');
            setIsLoading(false);
        }
    }, [phone, onConfirmed]);

    const onInputSms = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;

        if (value.length <= CODE_LENGTH) {
            setSmsCode(value);

            if (value.length === CODE_LENGTH) {
                onValidateCode(value);
            }
        }
    }, [onValidateCode]);

    useEffect(() => {
        if (inputRef?.current && isCodeSended) {
            inputRef.current.focus();
        }
    }, [inputRef, isCodeSended]);

    useEffect(() => {
        return () => {
            stop();
            setSmsCode('');
            setIsLoading(false);
            setIsSmsRequestOnceSent(false);
            setCodeValidationErrorMessage('');
        };
    }, []);

    return (
        <ST.VerificationBlock>
            <Paragraph4 $smaller>
                {verifyType === 'sms'
                    ? 'Введите код из смс'
                    : 'Введите последние 4 цифры номера, с которого вам только что позвонили'
                }
            </Paragraph4>
            <ST.CodeInput
                ref={inputRef}
                value={smsCode}
                placeholder={'Код'}
                onChange={onInputSms}
                errorMessage={codeValidationErrorMessage}
                isNotValid={smsCode.length === CODE_LENGTH && !!codeValidationErrorMessage}
                disabled={isLoading}
            />
            <ButtonWithTimer
                isLoading={isLoading}
                autoStart={isSmsRequestOnceSent}
                status={timeStatus}
                seconds={seconds}
                onStart={start}
                onBtnClick={onRequestCode}
            />
        </ST.VerificationBlock>
    );
};

export default PhoneVerifyForm;
